/**
 * Boss / enemy mechanics (+encounters). OWNER: encounters workflow.
 *
 * - `createMechanicsSystem()` implements `MechanicsSystem`: combat routes every `{ type: 'mechanic' }`
 *   effect to `apply`; the AI calls `update` once per tick at the end of `ai.update` (before casting,
 *   movement, zones and telegraphs).
 * - The AI also calls the plain helpers re-exported below (gating, once/chain, engage/death,
 *   intermissions). They only touch `world.mechanics`, unit AI timers and normal subsystem APIs.
 *
 * All live state is in `world.mechanics` (serializable); all randomness comes from ctx.rng.
 */
import type { EffectCtx, MechanicEffect, MechanicSpec, MechanicsSystem, SimContext } from '../../contracts';
import { updateCastRules } from './abilities';
import { updateAuraWatch } from './auraWatch';
import { applyHazard, updateHazards } from './hazards';
import { cleanupIntermission } from './intermission';
import { applyCharge, applyPull, applyTether, applyWave, updateCharges, updatePulls, updateTethers } from './movers';
import { applyFixate, applyGate, applyGaze, applyHealAbsorb, applyKnockback, applyPick, applyShield } from './simple';
import { applySoak, updateSoaks } from './soak';
import { updateLinks } from './units';

export { abilityUsable, hpWindowFor, onAbilityUsed, onTimersScheduled, updateCastRules } from './abilities';
export { collectMechanicAuras, type MechanicAuraSources } from './collect';
export { startIntermission, updateIntermission } from './intermission';
export { onEngage, onUnitDeath } from './units';
export { isLookingAt, setFixate } from './simple';
export { consumeStacks } from './soak';
export { DAMAGE_LINK_ABILITY_ID, gatePasses, keyLevel, mstate, pickTargets, resolveKeyScaled } from './state';

/** Run one mechanic spec now. */
export function applyMechanic(ctx: SimContext, spec: MechanicSpec, ectx: EffectCtx): void {
  switch (spec.kind) {
    case 'pick':
      return applyPick(ctx, spec, ectx);
    case 'soak':
      return applySoak(ctx, spec, ectx);
    case 'pull':
      return applyPull(ctx, spec, ectx);
    case 'knockback':
      return applyKnockback(ctx, spec, ectx);
    case 'hazard':
      return applyHazard(ctx, spec, ectx);
    case 'tether':
      return applyTether(ctx, spec, ectx);
    case 'charge':
      return applyCharge(ctx, spec, ectx);
    case 'gate':
      return applyGate(ctx, spec, ectx);
    case 'gaze':
      return applyGaze(ctx, spec, ectx);
    case 'fixate':
      return applyFixate(ctx, spec, ectx);
    case 'healAbsorb':
      return applyHealAbsorb(ctx, spec, ectx);
    case 'shield':
      return applyShield(ctx, spec, ectx);
    case 'wave':
      return applyWave(ctx, spec, ectx);
  }
}

/** One mechanics tick. Order: casts → links → aura hooks → displacement → tethers → charges → soaks → hazards. */
export function updateMechanics(ctx: SimContext): void {
  updateCastRules(ctx);
  updateAuraWatch(ctx);
  updateHazards(ctx);
  if (!ctx.world.mechanics) return;
  updateLinks(ctx);
  updatePulls(ctx);
  updateTethers(ctx);
  updateCharges(ctx);
  updateSoaks(ctx);
  cleanupIntermission(ctx);
}

export function createMechanicsSystem(): MechanicsSystem {
  return {
    apply(ctx: SimContext, effect: MechanicEffect, ectx: EffectCtx) {
      if ((ectx.depth ?? 0) > 4) return;
      applyMechanic(ctx, effect.spec, ectx);
    },
    update(ctx: SimContext) {
      updateMechanics(ctx);
    },
  };
}
